import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { CSSProperties, ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'

import { DragHandle } from './DragHandle'
import type { SortableRenderState } from './types'

export interface SortableItemProps {
  id: string
  /** Índice dentro de su lista. Se publica como `data-dnd-index`. */
  index: number
  /** Nombre humano de lo que se arrastra, para el `aria-label` del handle. */
  label: string
  disabled?: boolean
  disabledReason?: string
  /** Id de la columna del kanban. `undefined` en una lista simple. */
  columnId?: string
  columnIndex?: number
  className?: string
  children: (state: SortableRenderState) => ReactNode
}

/**
 * Un item arrastrable.
 *
 * Expone el handle YA cableado al activador de dnd-kit y deja que el consumidor
 * lo ubique: en `/priorities` va antes del número de rank, en una tarjeta de
 * kanban va arriba a la izquierda. Los atributos `data-dnd-*` no son decorativos
 * — los tests los usan para darle una geometría sintética a jsdom, que no hace
 * layout.
 */
export function SortableItem({
  id,
  index,
  label,
  disabled = false,
  disabledReason,
  columnId,
  columnIndex = 0,
  className,
  children,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id,
    disabled,
    data: { index, columnId, type: 'item' },
  })

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  }

  const handle = (
    <DragHandle
      ref={setActivatorNodeRef}
      label={label}
      disabled={disabled}
      disabledReason={disabledReason}
      isDragging={isDragging}
      {...attributes}
      {...listeners}
    />
  )

  return (
    <div
      ref={setNodeRef}
      style={style}
      role="listitem"
      data-dnd-index={index}
      data-dnd-column-index={columnIndex}
      data-dragging={isDragging || undefined}
      className={cn(
        // Mientras se arrastra, el item se atenúa en lugar de levantarse con
        // sombra: la jerarquía de "Consola" es hairline + superficie.
        isDragging && 'relative z-10 opacity-60',
        className,
      )}
    >
      {children({ index, isDragging, isSorting, disabled, handle })}
    </div>
  )
}
